import Utils from '../../../../../../utils/descriptor/descriptorUtils';
import EntityFactory from './cellEntity';
import ObjCheck from '../../../../../../utils/objCheck';
import ReportUtils from '../../../../../../utils/reportUtils';
import { MetadataField, ReportField } from '../../../../../../constnats/reportConstants';
import { Button, Checkbox, Container, Modal, Table } from 'semantic-ui-react';
import ReportsRepo from '../../../../../../utils/repository/repoertsRepo';
import { Fragment, useState } from 'react';
import Loader from '../../../../../../components/loader';
import { DescriptorProps } from '../../../../../../constnats/descriptor';
import NumUtils from '../../../../../../utils/utils'
import Config from '../../../../../../constnats/config';
import DescriptorType from '../../../../../../utils/descriptor/descriptorType';

const UPDATA_STRATEGY = {
    REPLACE_CORE_VALUES: "REPLACE_CORE_VALUES",
    PRESERVE_NON_EMPTY_CORE_VALUES: "PRESERVE_NON_EMPTY_CORE_VALUES",
    APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES: "APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES"
}

const REQUIRED_DESCRIPTORS_MAP = {
    [ReportField.YEAR]: "Year",
    [ReportField.PERIOD]: "Period",
    [ReportField.TYPE]: "Filling Type",
    [ReportField.REPORT_TYPE]: "Report Type"
}

function getReportChanges(report, descriptors, descriptorsToUpdateStrategyMap) {
    // Descriptor ID => { new: {value, correction, calcsDisabled}, old: {value, correction, calcsDisabled} }
    const reportChanges = {}

    const changeListener = (descriptorChange, entity) => {
        const descriptorId = entity.descriptorId()
        const oldDataRaw = report[ReportField.DYNAMIC_FIELDS][descriptorId]
        const oldData = oldDataRaw === undefined ? "" : oldDataRaw
        const fieldSpecificMetadata = report[ReportField.METADATA].fieldSpecific[descriptorId]
        const oldCorrectionData = fieldSpecificMetadata?.[MetadataField.CORRECTION] || "0"
        const oldCalcsDisabled = !!(fieldSpecificMetadata?.[MetadataField.CALCS_DISABLED])
        const oldManuallyCalculated = !!(fieldSpecificMetadata?.[MetadataField.MANUALLY_CALCED])

        if (descriptorChange === true) {
            const newMetadata = entity.getMetadata()
            const newData = entity.getData()
            const newCorrectionData = newMetadata[MetadataField.CORRECTION]
            const newCalcsDisabled = entity.getCalcsDisabled()
            const newManuallyCalculated = !!newMetadata[MetadataField.MANUALLY_CALCED]

            if (newData === "-" &&
                ObjCheck.isNullUndefinedEmptyOrDash(oldData)
                && newCorrectionData == oldCorrectionData
                && newCalcsDisabled == oldCalcsDisabled) {
                //TODO Find more elegant approach
            } else {
                reportChanges[descriptorId] = {
                    new: { value: newData, correction: newCorrectionData, calcsDisabled: newCalcsDisabled, manuallyCalculated: newManuallyCalculated},
                    old: { value: oldData, correction: oldCorrectionData, calcsDisabled: oldCalcsDisabled, manuallyCalculated:  oldManuallyCalculated}
                }
            }
        }
    }

    /**
     * TODO remove object cloning once sure that the report is not modified in the EntryFactory. Assume it is not but needs to be confirmed.
     */
    const dryRunEntries = new EntityFactory(changeListener)
        .create(descriptors, JSON.parse(JSON.stringify(report)))

    for (const entity of dryRunEntries) {
        entity.refresh()
    }

    for (const entity of dryRunEntries) {
        const descriptor = entity.getDescriptor();
        const descriptorId = descriptor[DescriptorProps.ID]
        let oldData = entity.getOriginalData().replace("%", "")
        const newData = entity.getData().replace("%", "")
        if (newData !== oldData) {
            /**
             * descriptorsToUpdateStrategyMap holds dynamic descriptorIds (it missing year period format etc).
             * Due to this we get an error if we try and access a non-dynamic descriptor id.
             * Here we check if the descriptorsToUpdateStrategyMap has a value before checking to see if the strategy
             * is included
             */
            if (descriptorsToUpdateStrategyMap[descriptorId]?.includes(UPDATA_STRATEGY.PRESERVE_NON_EMPTY_CORE_VALUES)) {
                if (Utils.hasCalculation(descriptor) && NumUtils.isNumber(oldData)) {
                    if (NumUtils.isNumber(newData)) {
                        let correction = ((NumUtils.toNumber(newData) - NumUtils.toNumber(oldData))) * -1;
                        correction = correction + NumUtils.toNumber(entity.getMetadata()[MetadataField.CORRECTION])
                        entity.setMetadata({
                            ...entity.getMetadata(),
                            [MetadataField.CORRECTION]: NumUtils.toReadMode(correction, Config.FLOATING_POINT_PRECISION)
                        })
                    } else {
                        entity.setMetadata({ ...entity.getMetadata(), [MetadataField.CALCS_DISABLED]: true })
                        if(DescriptorType.isPercent(descriptor)) {
                            oldData = oldData + "%"
                        }
                        entity.setData(oldData)
                    }
                }
            }

            if (descriptorsToUpdateStrategyMap[descriptorId]?.includes(UPDATA_STRATEGY.APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES)) {
                if (ObjCheck.isNullUndefinedEmptyOrDash(oldData)) {
                    entity.setMetadata({
                        ...entity.getMetadata(),
                        [MetadataField.MANUALLY_CALCED]: true
                    })
                }
            }
        }
    }

    const updatedReport = ReportUtils
        .cellEntitiesToReport(dryRunEntries, report)

    return { updatedReport, changes: reportChanges }
}

function reportsChangesToMatrix(changedReports, affectedFieldsIds, dynamicFlatDescriptors) {
    // Descriptor ID => Changes
    const allChangesMatrix = {}

    for (const id of Object.keys(REQUIRED_DESCRIPTORS_MAP)) {
        allChangesMatrix[id] = [REQUIRED_DESCRIPTORS_MAP[id]]
    }

    for (const descriptorId of affectedFieldsIds) {
        allChangesMatrix[descriptorId] = []
    }

    for (const changedReport of changedReports) {
        for (const id of Object.keys(REQUIRED_DESCRIPTORS_MAP)) {
            allChangesMatrix[id].push(changedReport.updatedReport[id])
        }

        for (const descriptorId of affectedFieldsIds) {
            const change = changedReport.changes[descriptorId]

            allChangesMatrix[descriptorId].push(change || null)
        }
    }

    const rows = []
    for (const id of Object.keys(REQUIRED_DESCRIPTORS_MAP)) {
        rows.push({
            descriptorId: id,
            dynamic: false,
            cells: [...allChangesMatrix[id]]
        })
    }

    for (const descriptor of dynamicFlatDescriptors) {
        const descriptorId = descriptor.id
        if (affectedFieldsIds.has(descriptorId)) {
            rows.push({
                descriptorId: descriptorId,
                dynamic: descriptor[DescriptorProps.DYNAMIC],
                cells: [descriptor.label, ...allChangesMatrix[descriptorId]]
            })
        }
    }

    return rows
}


function getChanges(descriptors, reports, descriptorsToUpdateStrategyMap) {
    descriptors = JSON.parse(JSON.stringify(descriptors))

    const affectedDescriptorIds = new Set()
    const changedReports = []
    for (const report of reports) {
        const reportChanges = getReportChanges(report, descriptors, descriptorsToUpdateStrategyMap)
        if (Object.keys(reportChanges.changes).length > 0) {
            changedReports.push(reportChanges)
            for (const descriptorId of Object.keys(reportChanges.changes)) {
                affectedDescriptorIds.add(descriptorId)
            }
        }
    }

    const dynamicFlatDescriptors = Utils.flatDescriptors(descriptors)
        .filter(d => d[DescriptorProps.DYNAMIC] === true)


    const rows = reportsChangesToMatrix(changedReports, affectedDescriptorIds, dynamicFlatDescriptors)

    return {
        rows,
        updatedReports: changedReports
            .filter(c => Object.keys(c.changes).length > 0)
            .map(c => c.updatedReport)
    }

}

function saveReports(updatedReports, schemaId, progressListener) {
    saveReport(updatedReports, 0, schemaId, progressListener)
}

function saveReport(updatedReports, idx, schemaId, progressListener) {
    if (updatedReports[idx] !== undefined) {
        ReportsRepo.update(Object.assign({ schemaId }, updatedReports[idx]), res => {
            progressListener.onSaved()
            saveReport(updatedReports, ++idx, schemaId, progressListener)
        })
    }
}

function getDescriptorsToUpdateStrategiesMap(descriptors) {
    return Utils.flatDescriptors(descriptors, true).reduce((acc, curr) => {
        acc[curr[DescriptorProps.ID]] = [];
        return acc
    }, {});
}

function BatchUpdateModal({ descriptors, schemaId, reports, closeModal, onSaved }) {
    const [descriptorsToUpdateStrategiesMap, setDescriptorsToUpdateStrategiesMap] =
        useState(() => getDescriptorsToUpdateStrategiesMap(descriptors))

    const handleUpdateStrategies = (descriptorId, strategy) => {
        setDescriptorsToUpdateStrategiesMap(prev => {
            const currentStrategies = prev[descriptorId];
            const isNewStrategyIncluded = currentStrategies.includes(strategy);

            const newDescriptorsToStrategiesMap = {
                ...prev,
                [descriptorId]: isNewStrategyIncluded
                    ? currentStrategies.filter(s => s !== strategy)
                    : [...currentStrategies, strategy]
            };

            return newDescriptorsToStrategiesMap
        })
    }
    return <BatchUpdateModalInternal {...getChanges(descriptors, reports, descriptorsToUpdateStrategiesMap)}
        closeModal={closeModal} schemaId={schemaId} onSaved={onSaved}
        onUpdateStrategyChange={handleUpdateStrategies}
        descriptorsToUpdateStrategyMap={descriptorsToUpdateStrategiesMap} />
}

function BatchUpdateModalInternal({ rows, updatedReports, schemaId, closeModal, onSaved, onUpdateStrategyChange,
    descriptorsToUpdateStrategyMap }) {
    const [syncInProgress, setSyncInProgress] = useState(false);
    const [progressListener] = useState(() => {
        let updated = 0
        return {
            onSaved: () => {
                if (++updated === updatedReports.length) {
                    setSyncInProgress(false)
                    onSaved()
                }
            }
        }
    })
    const headerCellColSpan = rows[0].cells.length
    const hasChanges = updatedReports.length > 0
    return syncInProgress ? <Loader /> : <Modal size='large' open={true}>
        <Modal.Header>Changes that are going to be applied.</Modal.Header>
        <Modal.Content className="overflowYScroll">
            {hasChanges ?
                <>
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell collapsing className='bg-gray'>
                                    <div>Preserve</div>
                                    <div>CORE VALUES</div>
                                </Table.HeaderCell>
                                <Table.HeaderCell collapsing className='bg-gray' >
                                    <div>Mark</div>
                                    <div>3D Calced.</div>
                                </Table.HeaderCell>
                                <Table.HeaderCell className='bg-gray' colSpan={headerCellColSpan} />
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {rows.map((row) =>
                                <Row key={row.descriptorId} row={row} rId={row.descriptorId} onUpdateStrategyChange={onUpdateStrategyChange}
                                    descriptorsToUpdateStrategyMap={descriptorsToUpdateStrategyMap} />)}
                        </Table.Body>
                    </Table>
                    <Container>
                        <p>
                            <b>Preserve CORE VALUES</b>: When this option is checked all non empty CORE VALUES will be preserved. <b>Correction</b> or <b>Calculation Disabled</b> will be applied in order to preserve the original value.
                        </p>
                        <p>
                            <b>Mark 3D Calced.</b>: When this option is checked, the field was previously empty and now it has a value, it will automatically be marked as <b>3D Calculated</b>.
                        </p>

                    </Container>
                </> : <>All reports already synchronised with the Model</>}
        </Modal.Content>
        <Modal.Actions>
            {hasChanges &&
                <Button size='mini' primary onClick={() => {
                    setSyncInProgress(true)
                    saveReports(updatedReports, schemaId, progressListener)
                }}>
                    Save Changes
                </Button>}
            <Button size='mini' onClick={closeModal}>
                Close
            </Button>

        </Modal.Actions>
    </Modal>
}

const DIFF_PROPS = ["value", MetadataField.CORRECTION, MetadataField.CALCS_DISABLED, MetadataField.MANUALLY_CALCED]
function getCellData(cell) {
    let components = []
    if (typeof cell === 'string' || cell === null) {
        components.push(<Fragment key="1">{cell}</Fragment>)
    } else {
        for (const prop of DIFF_PROPS) {
            const newData = cell.new[prop]
            const oldData = cell.old[prop]
            if (newData !== oldData) {
                components.push(<Fragment key={prop}>

                    <p className='redText'>{prop}: {String(oldData)}</p>
                    <p className='greenText'>{prop}: {String(newData)}</p>
                </Fragment>)
            }
        }
    }

    return components
}

function Row({ row, rId, onUpdateStrategyChange, descriptorsToUpdateStrategyMap }) {
    const currentStrategy = descriptorsToUpdateStrategyMap[row.descriptorId]

    return <Table.Row key={rId}>
        <Table.Cell textAlign='center'>
            {row.dynamic &&
                <Checkbox
                    checked={currentStrategy.includes(UPDATA_STRATEGY.PRESERVE_NON_EMPTY_CORE_VALUES)}
                    onClick={() =>
                        onUpdateStrategyChange(row.descriptorId, UPDATA_STRATEGY.PRESERVE_NON_EMPTY_CORE_VALUES)
                    }
                />
            }
        </Table.Cell>
        <Table.Cell textAlign='center'>
            {row.dynamic &&
                <Checkbox
                    checked={currentStrategy.includes(
                        UPDATA_STRATEGY.APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES
                    )}
                    onClick={() =>
                        onUpdateStrategyChange(
                            row.descriptorId,
                            UPDATA_STRATEGY.APPLY_MANUALLY_CALCULATED_TO_PREVIOUSLY_EMPTY_CORE_VALUES
                        )
                    }
                />
            }
        </Table.Cell>
        {row.cells.map((cell, cId) =>
            <Table.Cell key={cId}>{
                getCellData(cell)
            }</Table.Cell>
        )}
    </Table.Row>
}

export default BatchUpdateModal